import React, { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import StudentDetailsLayout from "../../components/StudentDetails/StudentDetailsLayout";
import StudentInfoCard from "../../components/StudentDetails/cards/StudentInfoCard";
import StudentCallAction from "../../components/StudentDetails/cards/StudentCallAction";
import CallManagementCard from "../../components/StudentDetails/cards/CallManagementCard";
import OtpPasswordManagementCard from "../../components/StudentDetails/cards/OtpPasswordManagementCard";
import OrderManagementCard from "../../components/StudentDetails/cards/OrderManagementCard";
import StudentProfileSelectionModal from "../../components/StudentDetails/StudentProfileSelectionModal";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import Loader from "../../components/Loader/Loader";
import routes from "../../constants/routes";
import tokenUtils from "../../utils/tokenUtils";
import searchUserResponseAdapter from "../../adapters/searchUserAdapter";
import StudentNotFoundState from "./components/StudentNotFoundState";
import useStudentProfileResolution from "./hooks/useStudentProfileResolution";
import useStudentDetailsQuery from "./hooks/useStudentDetailsQuery";
import useStudentRouteContext from "./hooks/useStudentRouteContext";

const StudentDetailsContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [modalDismissed, setModalDismissed] = useState(false);

  const {
    stateStudentData,
    statePhone,
    stateBranchId,
    stateBranchName,
    vertical,
    verticalInfo,
    features,
    decryptedPhone,
    urlUamId,
    urlAdmissionNumber,
  } = useStudentRouteContext(location);

  const {
    data: queryResult,
    isLoading,
    error,
    shouldFetch,
  } = useStudentDetailsQuery({
    stateStudentData,
    vertical,
    decryptedPhone,
    urlUamId,
    urlAdmissionNumber,
  });

  const { localStudentData, localPhone, profilesForSelection } =
    useStudentProfileResolution({
      stateStudentData,
      statePhone,
      queryResult,
      decryptedPhone,
      verticalInfo,
      urlUamId,
      urlAdmissionNumber,
    });

  const handleBack = useCallback(() => {
    navigate(routes.home.root);
  }, [navigate]);

  const handleProfileSelect = useCallback(
    ({ account, profile }) => {
      navigate(location.pathname + location.search, {
        state: {
          studentData: searchUserResponseAdapter(account, profile),
          phone: account.phone || localPhone,
          vertical,
        },
        replace: true,
      });
    },
    [localPhone, location.pathname, location.search, navigate, vertical],
  );

  const handleProfilesModalClose = useCallback(() => {
    setModalDismissed(true);
    if (!localStudentData) navigate(routes.home.root);
  }, [localStudentData, navigate]);

  const errorMessage =
    error?.message ||
    (!isLoading &&
    !localStudentData &&
    !(profilesForSelection.length > 0 && !modalDismissed) &&
    shouldFetch
      ? "No user found with the provided details for the selected vertical."
      : "") ||
    (!vertical && !stateStudentData ? "Vertical is missing in URL." : "");

  const showOtpCard =
    features?.getOtp ||
    features?.resetOtp ||
    features?.unblock ||
    features?.updatePassword;
  const adminId = tokenUtils.getUserDetails()?.userId;

  if (isLoading) return <Loader fullScreen />;

  if (
    !localStudentData &&
    !(profilesForSelection.length > 0 && !modalDismissed)
  ) {
    return (
      <StudentNotFoundState errorMessage={errorMessage} onBack={handleBack} />
    );
  }

  const operationGroups = [
    showOtpCard
      ? {
          title: "OTP and Password",
          content: (
            <OtpPasswordManagementCard
              phone={localPhone}
              features={features}
              studentData={localStudentData}
              verticalInfo={verticalInfo}
              branchId={stateBranchId}
              branchName={stateBranchName}
            />
          ),
        }
      : null,
    features?.call
      ? {
          title: "Call Management",
          content: (
            <CallManagementCard
              uamId={localStudentData?.uam_id}
              adminId={adminId}
            />
          ),
        }
      : null,
    features?.orderManagement
      ? {
          title: "Order Management",
          content: (
            <OrderManagementCard
              uamId={localStudentData?.uam_id}
              phone={localPhone}
              vertical={vertical}
            />
          ),
        }
      : null,
  ].filter(Boolean);

  return (
    <>
      {localStudentData && (
        <StudentDetailsLayout
          vertical={vertical}
          studentData={localStudentData}
          studentInfoSlot={
            <StudentInfoCard
              studentData={localStudentData}
              phone={localPhone}
            />
          }
          callActionSlot={
            features?.call ? (
              <StudentCallAction
                studentData={localStudentData}
                verticalInfo={verticalInfo}
              />
            ) : null
          }
          operationGroups={operationGroups}
        />
      )}

      <StudentProfileSelectionModal
        open={profilesForSelection.length > 0 && !modalDismissed}
        onClose={handleProfilesModalClose}
        profiles={profilesForSelection}
        onSelectProfile={handleProfileSelect}
      />

      <ErrorSnackbar
        open={!!errorMessage}
        message={errorMessage}
        onClose={() => {}}
      />
    </>
  );
};

export default StudentDetailsContainer;
